import type { UnknownAction } from '@reduxjs/toolkit';
import { hash } from 'immutable';

import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import { coreActions } from '../core-actions';
import * as actions from './tagsActions';
import type { TagProps } from './tagsTypes';

export const QcsSyncTagsStoreFactory = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<TagProps>({
  resourceName: 'TAGS',
});

function reducer(
  state: QcsSyncResourceStore<TagProps> = QcsSyncTagsStoreFactory(),
  action: UnknownAction,
): typeof state {
  switch (true) {
    case actions.getTags.match(action):
      return state.set('isLoading', true);
    case actions.getTagsSuccess.match(action):
      return resourceUpserters.upsertResources(state, action.payload);
    case actions.getTagsFailure.match(action):
      return resourceUpserters.completeWithError(state, action.payload);

    case coreActions.SANITIZE === action.type:
      return state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.merge({
            name: hash(resource.name).toString(),
          }),
        ),
      );

    case actions.createTag.match(action):
      return resourceUpserters.upsertResource(state, action.payload);
    case actions.createTagSuccess.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.deleteTag);

    case actions.updateTag.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.updateTag);
    case actions.updateTagSuccess.match(action):
      return resourceUpserters.upsertResource(state, action.payload);

    case actions.deleteTag.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createTag);
    case actions.deleteTagSuccess.match(action):
      return resourceUpserters.upsertResource(state, action.payload);

    case actions.batchTags.match(action):
      return resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchTags, false);
    case actions.batchTagsSuccess.match(action):
      return resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchTags, true);

    case actions.createTagFailure.match(action):
    case actions.updateTagFailure.match(action):
    case actions.deleteTagFailure.match(action):
    case actions.batchTagsFailure.match(action):
      return resourceUpserters.resetResources(state, action.payload);

    default:
      return state;
  }
}

export default reducer;
